import { Timestamp } from 'firebase/firestore';

import { DirectoryCurationSchema } from '@kilat/shared';

import { createRepo } from './_base.js';

/**
 * directoryCuration/{outletId} — PRIVATE curation reasons for the public
 * outletDirectory rows. Super-admin read/write only (firestore.rules); the
 * note must never ride the world-readable public row.
 */
const repo = createRepo({
  path: 'directoryCuration',
  schema: DirectoryCurationSchema,
  name: 'directoryCuration',
});

/**
 * Field-scoped upsert: update() patches only the given fields (so two
 * concurrent super-admin actions — hide toggle + note edit — can't clobber
 * each other, plan-audit 2026-07-16); a missing doc falls back to a full
 * create with schema defaults.
 */
async function upsertFields(
  outletId: string,
  patch: { hidden?: boolean; note?: string },
): Promise<void> {
  const stamped = { ...patch, updatedAt: Timestamp.now() };
  try {
    await repo.update(outletId, stamped);
  } catch {
    await repo.set(outletId, {
      outletId,
      hidden: patch.hidden ?? false,
      note: patch.note ?? '',
      updatedAt: Timestamp.now(),
    });
  }
}

export const directoryCurationRepo = {
  ...repo,
  /** Upsert the note for an outlet row (doc id === outletId); `hidden` untouched. */
  saveNote(outletId: string, note: string): Promise<void> {
    return upsertFields(outletId, { note: note.slice(0, 300) });
  },
  /**
   * Upsert the hide flag — the SOURCE OF TRUTH the directorySync projection
   * reads on every rebuild (the public row alone is operator-deletable via an
   * opt-in cycle; this doc is not). `note` untouched.
   */
  setHidden(outletId: string, hidden: boolean): Promise<void> {
    return upsertFields(outletId, { hidden });
  },
};
